<script lang="ts">
    import type {Document, LabeledIdentified} from 'typings/portaro.be.types';
    import type {IssueAwareVolume, KpVolumesComponentCapabilities} from './types';
    import type ExemplarFilter from '../exemplar-filter';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpVolumesPresenter} from './kp-volumes.presenter';
    import {onMount} from 'svelte';
    import {fade} from 'svelte/transition';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import VolumeAccordion from './VolumeAccordion.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import {volumesAccordionSlideAnimationDurationMs} from './defaults';
    import {exists} from 'shared/utils/custom-utils';

    export let document: Document;
    export let exemplarsListProperties: LabeledIdentified<string>[];
    export let exemplarStatuses: number[];
    export let capabilities: KpVolumesComponentCapabilities;

    const localize = getLocalization();
    const presenter = getInjector().getByToken<KpVolumesPresenter>(KpVolumesPresenter.presenterName);

    let volumes: IssueAwareVolume[] = [];
    let exemplarFilter: ExemplarFilter | undefined;
    let loading = true;

    $: isEmpty = (!exists(volumes) || volumes.length === 0) && !capabilities.canCreateAnyExemplar && !capabilities.canCreateAnyVolume;
    $: canEdit = capabilities.canEditAnyExemplar || capabilities.canCreateAnyExemplar || capabilities.canCreateAnyVolume || capabilities.canCreateAnyVolume;

    onMount(async () => {
        const data = await presenter.initializeComponentData(document, exemplarStatuses);
        exemplarFilter = data.exemplarFilter;
        volumes = data.volumes;
        loading = false;
    });

    const handleFirstVolumeOpen = async (volume: IssueAwareVolume) => {
        volume.issues = await presenter.loadIssues(volume, exemplarFilter);
        // The above code won't trigger reactive changes, because volume is in a list.
        // And because there is also a slide animation and height size animation,
        // we need to wait for the first slide animation to stop playing,
        // because otherwise it would cause a collision between the two
        // mentioned animations if the issues were loaded before slide stops.
        setTimeout(triggerVolumesListReactivity, volumesAccordionSlideAnimationDurationMs);
    }

    const triggerVolumesListReactivity = () => {
        volumes = volumes;
    }

    function countVolumes(countedVolumes: IssueAwareVolume[]): number {
        return countedVolumes.filter((v) => canEdit || v.withIssues).length;
    }
</script>

{#if loading}
    <KpLoadingBlock size="sm"/>
{/if}

{#if !loading && !isEmpty}
    <div class="kp-volumes">
        <div class="volumes-heading">
            {#if capabilities.canCreateAnyExemplar || capabilities.canCreateAnyVolume || !loading && volumes.length > 0 && presenter.haveVolumesAnyIssue(volumes)}
                <h2 class="unset-style volumes-title">{localize(/* @kp-localization commons.rocniky */ 'commons.rocniky')}</h2>
                <span class="badge volumes-count-badge">{countVolumes(volumes)}</span>
            {/if}

            {#if capabilities.canCreateAnyVolume}
                <div class="new-volume-button-container">
                    <KpButton buttonSize="sm" on:click={() => presenter.showVolumeCreation(document)}>
                        <IconedContent icon="add">
                            {localize(/* @kp-localization volume.NovyRocnik */ 'volume.NovyRocnik')}
                        </IconedContent>
                    </KpButton>
                </div>
            {/if}
        </div>

        {#if volumes.length > 0}
            <ul class="volumes-list" in:fade>
                {#each volumes as volume(volume.id)}
                    {#if canEdit || volume.withIssues}
                        <VolumeAccordion {document}
                                         {presenter}
                                         {volume}
                                         {capabilities}
                                         {exemplarsListProperties}
                                         on:first-open={() => handleFirstVolumeOpen(volume)}/>
                    {/if}
                {/each}
            </ul>
        {/if}
    </div>
{/if}

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    .kp-volumes {
        display: flex;
        flex-direction: column;
        gap: @padding-large-vertical;

        .volumes-heading {
            display: flex;
            align-items: center;
            gap: @padding-xs-horizontal;

            .volumes-title {
                font-size: @font-size-h3;
            }

            .volumes-count-badge {
                background-color: @gray-dark;
            }

            .new-volume-button-container {
                margin-left: auto;
            }
        }

        .volumes-list {
            display: flex;
            flex-direction: column;
            gap: @padding-large-vertical;
        }
    }
</style>